import { useState, useEffect, useMemo } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { NAV_GROUPS } from "@/config/nav";
import { getIcon } from "@/config/iconMap";
import { navigationService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { NotificationCenter } from "@/components/layout/NotificationCenter";
import { useI18n } from "@/context/I18nContext";
import { LogOut, Sparkles, Search, Menu, X, ChevronDown } from "lucide-react";

// Convert the static fallback config into the dynamic nav shape.
function staticFallback(hasPermission) {
  return NAV_GROUPS.map((g) => ({
    group: g.group,
    items: g.items
      .filter((i) => hasPermission(i.perm))
      .map((i) => ({ label: i.label, route: i.to, icon: null, _staticIcon: i.icon })),
  })).filter((g) => g.items.length > 0);
}

export default function AdminShell() {
  const { user, hasPermission, logout } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    let active = true;
    navigationService
      .resolve()
      .then((d) => { if (active) setGroups(d.groups || []); })
      .catch(() => { if (active) setGroups(staticFallback(hasPermission)); });
    return () => { active = false; };
  }, [hasPermission]);

  const labelMap = useMemo(() => {
    const m = {};
    groups.forEach((g) => g.items.forEach((i) => { if (i.route) m[i.route] = i.label; }));
    return m;
  }, [groups]);

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        className={`bg-sidebar text-slate-200 w-64 flex-shrink-0 fixed lg:static inset-y-0 left-0 z-40 transform transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          <div className="h-8 w-8 rounded-lg bg-sidebar-active flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-slate-900" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <div className="font-extrabold tracking-tight text-white text-[15px]">REASON ConnectIQ</div>
            <div className="text-[10px] text-sidebar-muted mt-0.5">{t("common.controlPlane", "Control Plane")}</div>
          </div>
        </div>
        <nav className="px-3 py-4 space-y-5 overflow-y-auto h-[calc(100vh-4rem)]">
          {groups.map((g) => (
            <div key={g.group}>
              <p className="px-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted mb-1.5">
                {g.group}
              </p>
              <div className="space-y-0.5">
                {g.items.map((item) => {
                  const Icon = item._staticIcon || getIcon(item.icon);
                  const slug = item.route.split("/").pop();
                  return (
                    <NavLink
                      key={item.route}
                      to={item.route}
                      onClick={() => setOpen(false)}
                      data-testid={`nav-${slug}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-sidebar-active text-slate-900"
                            : "text-slate-300 hover:bg-white/5 hover:text-white"
                        }`
                      }
                    >
                      <Icon className="h-4 w-4" />
                      {t(`nav.${slug}`, item.label)}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 lg:px-7 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="sidebar-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div className="hidden md:flex items-center gap-2 bg-muted rounded-lg px-3 py-2 w-72">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                placeholder={t("common.search", "Search…")}
                className="bg-transparent text-sm outline-none w-full text-foreground"
                data-testid="global-search"
              />
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <LanguageSwitcher />
            <ThemeToggle />
            <NotificationCenter />
            <ProfileMenu user={user} logout={logout} />
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto animate-fade-in">
            <Breadcrumb labelMap={labelMap} />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function ProfileMenu({ user, logout }) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  const initials = `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}` || "SA";
  return (
    <div className="relative">
      <button
        onClick={() => setShow(!show)}
        className="flex items-center gap-2.5 hover:bg-muted rounded-lg px-2 py-1.5 transition-colors"
        data-testid="profile-menu-button"
      >
        <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
          {initials.toUpperCase()}
        </div>
        <div className="hidden sm:block text-left leading-tight">
          <div className="text-sm font-semibold text-foreground">
            {user?.firstName} {user?.lastName}
          </div>
          <div className="text-[11px] text-muted-foreground">{user?.isSuperAdmin ? "SuperAdmin" : (user?.roleCodes || [])[0]}</div>
        </div>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>
      {show && (
        <div className="absolute right-0 mt-2 w-52 bg-popover text-popover-foreground rounded-xl border border-border shadow-lg py-1.5 z-30" data-testid="profile-dropdown">
          <div className="px-4 py-2 border-b border-border">
            <div className="text-sm font-medium text-foreground truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> {t("action.signOut", "Sign out")}
          </button>
        </div>
      )}
    </div>
  );
}
